import { useCallback, useMemo } from 'react';

import { useSelectedColor } from '../useSelectedColor';
import {
  CornerInfoWithProductInfo,
  CustomCutoutWithFullRecord,
  DesignProductMatchingSummary,
  DrawingShapesSummary,
  ProductMatchingReturnParameters,
  ProductMatchingSummary,
  ProductMatchingTargetParameters,
  SelectedCutoutsProductMatchingSummary,
  SelectedItemWithFullRecord,
} from './interfaces/ProductMatchingSummary';
import {
  computeChangedCookingAppliances,
  computeNextSinkSelections,
  reconcileSideApplianceIds,
} from './productMatchingSideAppliances.utils';
import {
  applyEdgeResetsToShapes,
  clearChipMinimizerFlagsOnShapes,
  createCutoutsToSummaryReducer,
  createDrawingShapesToSummaryReducer,
  createDrawingShapesToSummaryReducerUsingApplianceChecker,
  createDrawingShapesToSummaryReducerUsingCornersChecker,
  createPlumbingToSummaryReducer,
  createSelectedItemToSummaryReducer,
  createTearOutsToSummaryReducer,
  findCatalogProductWithFallback,
  getEdgesToReset,
  getFullRecordWithSavedFallback,
  getSideItemTypesToRemove,
  isSelectedItemWithPropertiesAndIsSelected,
} from './utils/useEquivalentProductMatching.utils';

import { CommonProductQueryParams } from '~/api/easy-estimator-api/commonProductQueryParams';
import { useLazyFetchCornerRadiiQuery } from '~/api/easy-estimator-api/corner-radii-api/cornerRadiiService';
import { useCornerRadii } from '~/api/easy-estimator-api/corner-radii-api/hooks/useCornerRadii';
import { useLazyFetchDiagonalCornersQuery } from '~/api/easy-estimator-api/diagonal-corners-api/diagonalCornersService';
import { useDiagonalCorners } from '~/api/easy-estimator-api/diagonal-corners-api/useDiagonalCorners';
import { useLazyFetchEdgesQuery } from '~/api/easy-estimator-api/edges-api/edgesService';
import { selectFilteredEdges } from '~/api/easy-estimator-api/edges-api/useEdges';
import { parseMaterials } from '~/api/easy-estimator-api/materials-api/hooks/useMaterials';
import {
  selectDefaultMaterial,
  useSelectedMaterial,
} from '~/api/easy-estimator-api/materials-api/hooks/useSelectedMaterial';
import { useLazyFetchMaterialsQuery } from '~/api/easy-estimator-api/materials-api/materialsService';
import { useLazyFetchOptionalItemsQuery } from '~/api/easy-estimator-api/optional-items-api/optionalItemsService';
import { useOptionalItems } from '~/api/easy-estimator-api/optional-items-api/useOptionalItems';
import { useLazyFetchPlumbingQuery } from '~/api/easy-estimator-api/plumbing-api/plumbingService';
import { usePlumbing } from '~/api/easy-estimator-api/plumbing-api/usePlumbing';
import {
  useLazyFetchCustomerSuppliedSinksQuery,
  useLazyFetchSinksQuery,
} from '~/api/easy-estimator-api/sinks-api/sinksService';
import { useLazyFetchStoresQuery } from '~/api/easy-estimator-api/stores-api/storesService';
import { useSelectedStore } from '~/api/easy-estimator-api/stores-api/useSelectedStore';
import { useLazyFetchPredefinedCookingAppliancesQuery } from '~/api/local-api/cooking-appliances.api/cookingAppliancesService';
import { drawingTool } from '~/core/engine/DrawingTool';
import { ShapeType } from '~/core/enums';
import {
  ApplianceModel,
  ApplianceSource,
  ApplianceType,
  ChipMinimizerModel,
  CornerRadiusModel,
  CutoutModel,
  DrawingAppliance,
  DrawingShape,
  EdgeModel,
  OptionalProduct,
  PlumbingProduct,
  ProductCatalogMap,
  SelectedItemMap,
  SelectedItemProperties,
  TearOutModel,
} from '~/core/models';
import { OptionalItemsResponse } from '~/core/models/optional-items-response';
import { CustomCutoutRecord } from '~/core/types/appliances';
import { makeSinksSelector } from '~/core/utils';
import { selectBestMatchingColor } from '~/core/utils/selectBestMatchingColor';
import { isValidZipCode } from '~/core/utils/validate';
import {
  useDrawingShapeContext,
  useOptionalProductContext,
  useProductDetailsContext,
} from '~/legacy/easy-estimator-page/context';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectRestorableColor } from '~/redux/slices/color';
import {
  selectSelectedAppliance,
  setCookingAppliance,
} from '~/redux/slices/cookingAppliance';
import { selectCustomCutouts, setCustomCutouts } from '~/redux/slices/cutout';
import { updateNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { selectRestorableMaterial } from '~/redux/slices/material';
import {
  selectSelectedOptionalItemsAsSelectedItemMap,
  setOptionalItemsMap,
} from '~/redux/slices/optionalItems';
import {
  selectSelectedPlumbing,
  updateSelectedPlumbing,
} from '~/redux/slices/plumbing';
import {
  clearChipMinimizerFlags,
  selectNumberOfSelectedChipMinimizers,
  selectSelectedSinksRaw,
  setSelectedSinks,
} from '~/redux/slices/sink';
import { selectRestorableStoreId } from '~/redux/slices/store';
import {
  selectSelectedTearOuts,
  updateSelectedTearOuts,
} from '~/redux/slices/tearOut';
import { selectZipcode } from '~/redux/slices/zipcode';

const emptyArray = [] satisfies DrawingShape[] | SelectedItemWithFullRecord[];

const INITIAL_PRODUCT_MATCHING_SUMMARY = {
  newValueAfterReplacements: emptyArray,
  kept: emptyArray,
  replacedWithEquivalent: emptyArray,
  removed: emptyArray,
} as const;

export const matchingCheckSkipToken = 'MATCHING_CHECK_SKIP_TOKEN' as const;
export type MatchingCheckSkipToken = typeof matchingCheckSkipToken;

export const useEquivalentProductMatching = () => {
  const dispatch = useAppDispatch();
  const {
    getters: { drawingShapes },
    handlers: { updateDrawingShapes },
  } = useDrawingShapeContext();

  const optionalProductContext = useOptionalProductContext();
  const { restorableCatalog } = useProductDetailsContext();

  const { cornerRadii } = useCornerRadii();
  const { diagonalCorners } = useDiagonalCorners();
  const {
    tearOuts: currentTearOutsCatalog,
    cutouts: currentCutoutsCatalog,
    chipMinimizer: currentChipMinimizerCatalog,
    optionalItems: currentOptionalItemsCatalog,
  } = useOptionalItems();

  const { plumbingItems: currentPlumbingCatalog } = usePlumbing();

  /** RTK lazy fetch method for new catalog */
  const [fetchEdges] = useLazyFetchEdgesQuery();
  const [fetchCornerRadii] = useLazyFetchCornerRadiiQuery();
  const [fetchDiagonalCorners] = useLazyFetchDiagonalCornersQuery();
  const [fetchOptionalItemsQuery] = useLazyFetchOptionalItemsQuery();
  const [fetchStores] = useLazyFetchStoresQuery();
  const [fetchMaterials] = useLazyFetchMaterialsQuery();
  const [fetchSinks] = useLazyFetchSinksQuery();
  const [fetchCustomerSuppliedCookingAppliances] =
    useLazyFetchPredefinedCookingAppliancesQuery();
  const [fetchCustomerSuppliedSinksQuery] =
    useLazyFetchCustomerSuppliedSinksQuery();
  const [fetchPlumbing] = useLazyFetchPlumbingQuery();

  const currentZipCode = useAppSelector(selectZipcode);
  const selectedStore = useSelectedStore();
  const selectedMaterial = useSelectedMaterial();
  const { selectedValue: selectedColor } = useSelectedColor();
  const restorableStoreId = useAppSelector(selectRestorableStoreId);
  const restorableMaterial = useAppSelector(selectRestorableMaterial);
  const restorableColor = useAppSelector(selectRestorableColor);
  const selectedTearOuts = useAppSelector(selectSelectedTearOuts);
  const selectedCutouts = useAppSelector(selectCustomCutouts).selected;
  const selectedPlumbing = useAppSelector(selectSelectedPlumbing);
  const numberOfSelectedChipMinimizers = useAppSelector(
    selectNumberOfSelectedChipMinimizers
  );
  const selectedOptionalItemsAsItemMap = useAppSelector(
    selectSelectedOptionalItemsAsSelectedItemMap
  );

  const selectedSinksRaw = useAppSelector(selectSelectedSinksRaw);
  const selectedCooktop = useAppSelector(
    selectSelectedAppliance('selectedCooktopApplianceProduct')
  );
  const selectedRange = useAppSelector(
    selectSelectedAppliance('selectedRangeApplianceProduct')
  );

  const selectSinksTransformed = useMemo(() => makeSinksSelector(), []);
  const selectSinksCustomerSuppliedTransformed = useMemo(
    () => makeSinksSelector(),
    []
  );

  /**
   * Fetches all catalog data in parallel
   */
  const fetchAllCatalogData = useCallback(
    async (commonProductQueryParams: CommonProductQueryParams) => {
      const [
        { data: edgesNewCatalog },
        { data: optionalItemsNewCatalog },
        { data: _cornersNewCatalog },
        { data: diagonalCorners },
        { data: _sinksNewCatalog },
        { data: _customerSuppliedSinksNewCatalog },
        { data: cookingAppliancesNewCatalog },
        { data: plumbingNewCatalog },
      ] = await Promise.all([
        fetchEdges(commonProductQueryParams),
        fetchOptionalItemsQuery(commonProductQueryParams),
        fetchCornerRadii(commonProductQueryParams),
        fetchDiagonalCorners(commonProductQueryParams),
        fetchSinks(commonProductQueryParams),
        fetchCustomerSuppliedSinksQuery(commonProductQueryParams),
        fetchCustomerSuppliedCookingAppliances(commonProductQueryParams),
        fetchPlumbing(commonProductQueryParams),
      ]);

      return {
        edgesNewCatalog,
        optionalItemsNewCatalog,
        _cornersNewCatalog,
        diagonalCorners,
        _sinksNewCatalog,
        _customerSuppliedSinksNewCatalog,
        cookingAppliancesNewCatalog,
        plumbingNewCatalog,
      };
    },
    [
      fetchEdges,
      fetchOptionalItemsQuery,
      fetchCornerRadii,
      fetchDiagonalCorners,
      fetchSinks,
      fetchCustomerSuppliedSinksQuery,
      fetchCustomerSuppliedCookingAppliances,
      fetchPlumbing,
    ]
  );

  /**
   * Transforms and combines catalog data into final catalog structure
   */
  const transformCatalogData = useCallback(
    (rawCatalogData: {
      _cornersNewCatalog: CornerRadiusModel[] | undefined;
      diagonalCorners: CornerRadiusModel[] | undefined;
      _sinksNewCatalog: ApplianceModel[] | undefined;
      _customerSuppliedSinksNewCatalog: ApplianceModel[] | undefined;
      cookingAppliancesNewCatalog: ApplianceModel[] | undefined;
      optionalItemsNewCatalog: OptionalItemsResponse | undefined;
    }) => {
      const cornersNewCatalog = [
        ...(rawCatalogData._cornersNewCatalog ?? emptyArray),
        ...(rawCatalogData.diagonalCorners ?? emptyArray),
      ];

      const customerSuppliedSinksNewCatalog =
        selectSinksCustomerSuppliedTransformed(
          rawCatalogData._customerSuppliedSinksNewCatalog ?? emptyArray,
          ApplianceSource.PROGRAM,
          ApplianceType.SINK_LABOR
        );

      const sinksNewCatalog = selectSinksTransformed(
        rawCatalogData._sinksNewCatalog ?? emptyArray,
        ApplianceSource.PROGRAM,
        ApplianceType.SINK,
        true
      );

      const appliancesNewCatalog: ApplianceModel[] = [
        ...(sinksNewCatalog ?? emptyArray),
        ...(customerSuppliedSinksNewCatalog ?? emptyArray),
        ...(rawCatalogData.cookingAppliancesNewCatalog ?? emptyArray),
      ];

      const cutoutsNewCatalog =
        rawCatalogData.optionalItemsNewCatalog?.cutouts ?? emptyArray;
      const tearOutsNewCatalog =
        rawCatalogData.optionalItemsNewCatalog?.['tear-outs'] ?? emptyArray;

      const optionalItemsNewCatalog =
        rawCatalogData.optionalItemsNewCatalog?.others ?? emptyArray;

      return {
        cornersNewCatalog,
        appliancesNewCatalog,
        tearOutsNewCatalog,
        cutoutsNewCatalog,
        optionalItemsNewCatalog,
      };
    },
    [selectSinksCustomerSuppliedTransformed, selectSinksTransformed]
  );

  /**
   * Given one or more target parameters for zipCode, Store, Material, Color, fetch and select the appropriate targets
   * Any parameters not provided default to the appropriate current selection
   * Any parameters (specified target or current) that are no longer valid (i.e. store not in new zip code, material not available in new store)
   *  results in an appropriate default selection
   * @returns {undefined} if failing to fetch stores or materials. Otherwise, returns CommonProductQueryParams with validated values
   */
  const resolveProductMatchingParams = useCallback(
    async (
      args: ProductMatchingTargetParameters
    ): Promise<ProductMatchingReturnParameters | undefined> => {
      // use any of the given values OR the currently selected values
      const {
        zipCode = currentZipCode,
        storeId = selectedStore?.companyId,
        material: intendedMaterial = {
          description: selectedMaterial?.description,
        },
        color = {
          productId: selectedColor?.productId,
          masterSku: selectedColor?.masterSku,
          thickness: selectedColor?.thickness,
          name: selectedColor?.name,
          brand: selectedColor?.brand,
        },
      } = args;

      if (!isValidZipCode(zipCode)) {
        return undefined;
      }

      // fetch stores for the new zip. For the old it won’t fetch from the API because of RTK query cache
      const rawStores = await fetchStores({ params: { zipCode } }, true);

      if (rawStores.isError || !rawStores?.data?.length) {
        console.warn(`Error fetching stores for zip code ${zipCode}`);

        return undefined;
      }

      // and ensure the target store is in there, or select the default store for the new zip code
      // An explicit storeId wins; only fall back to restorableStoreId when
      // no explicit storeId was given (or it is not available in the new zip).
      const { data: stores } = rawStores;

      const findStoreByCompanyId = (companyId?: number) =>
        companyId
          ? stores.find((store) => store.companyId === companyId)
          : undefined;

      const newStore =
        findStoreByCompanyId(storeId) ??
        findStoreByCompanyId(restorableStoreId) ??
        stores[0];

      // fetch materials based on the validated zip and store
      const rawMaterials = await fetchMaterials(
        { params: { zipCode, storeId: newStore.companyId } },
        true
      );

      if (rawMaterials.isError || !rawMaterials?.data?.length) {
        console.warn(
          `Error fetching materials for zip code ${zipCode} and store ${newStore.companyId}`
        );

        return undefined;
      }

      //this is primarily so we're working with MaterialModel records
      const materials = parseMaterials(rawMaterials.data);

      // and try to find the target material (if changing materials), or the currently userSelectedMaterial currently exists
      // or select default material if not
      const matchingMaterial =
        materials?.find(
          ({ description }) =>
            intendedMaterial && description === intendedMaterial.description
        ) ??
        materials?.find(
          ({ description }) =>
            restorableMaterial && description === restorableMaterial.description
        ) ??
        selectDefaultMaterial({
          materials,
          selectedValue: selectedMaterial,
        });

      // colors come from whatever material we just found
      const colors = matchingMaterial?.colors ?? emptyArray;

      // find the best match for the given color, or the selectedColor
      const newColor = selectBestMatchingColor(color, colors, restorableColor);

      if (!newColor) {
        console.warn(
          `Error fetching colors for zip code ${zipCode}, store ${newStore?.companyId} and material ${matchingMaterial?.description}`
        );

        return undefined;
      }

      // now return all the _validated_ values - all subsequent api lookups should be from these
      return {
        zipCode,
        storeId: newStore.companyId,
        store: newStore,
        prodQteGrpID: newColor.qteGrpID,
        material: matchingMaterial,
        color: newColor,
      };
    },
    [
      currentZipCode,
      selectedStore?.companyId,
      selectedMaterial,
      selectedColor?.productId,
      selectedColor?.masterSku,
      selectedColor?.thickness,
      selectedColor?.name,
      selectedColor?.brand,
      fetchStores,
      fetchMaterials,
      restorableColor,
      restorableStoreId,
      restorableMaterial,
    ]
  );

  const fetchNewCatalogs = useCallback(
    async (
      commonProductQueryParams: CommonProductQueryParams
    ): Promise<{
      edgesNewCatalog: EdgeModel[] | undefined;
      cornersNewCatalog: CornerRadiusModel[] | undefined;
      appliancesNewCatalog: ApplianceModel[];
      chipMinimizersNewCatalog?: ChipMinimizerModel;
      cutoutsNewCatalog: CutoutModel[];
      tearOutsNewCatalog: TearOutModel[];
      optionalItemsNewCatalog: OptionalProduct[];
      plumbingNewCatalog: PlumbingProduct[] | undefined;
    }> => {
      const rawCatalogData = await fetchAllCatalogData(
        commonProductQueryParams
      );
      const transformedData = transformCatalogData(rawCatalogData);

      return {
        edgesNewCatalog: rawCatalogData.edgesNewCatalog,
        cornersNewCatalog: transformedData.cornersNewCatalog,
        appliancesNewCatalog: transformedData.appliancesNewCatalog,
        chipMinimizersNewCatalog:
          rawCatalogData.optionalItemsNewCatalog?.['chip-minimizer'],
        cutoutsNewCatalog: transformedData.cutoutsNewCatalog,
        tearOutsNewCatalog: transformedData.tearOutsNewCatalog,
        optionalItemsNewCatalog: transformedData.optionalItemsNewCatalog,
        plumbingNewCatalog: rawCatalogData.plumbingNewCatalog,
      };
    },
    [fetchAllCatalogData, transformCatalogData]
  );

  /**
   * Calculates product matching summary for edges
   */
  const calculateEdgeSummary = useCallback(
    (
      drawingShapes: DrawingShape[],
      edgesNewCatalog: EdgeModel[],
      newColorBrand?: string | null
    ): ProductMatchingSummary<EdgeModel, DrawingShape> => {
      const clonedDrawingShapes = drawingTool.cloneShapes(drawingShapes);

      return drawingShapes.reduce(
        createDrawingShapesToSummaryReducer(edgesNewCatalog, newColorBrand),
        {
          ...INITIAL_PRODUCT_MATCHING_SUMMARY,
          newValueAfterReplacements: clonedDrawingShapes,
        }
      );
    },
    []
  );

  /**
   * Calculates product matching summary for appliances
   */
  const calculateApplianceSummary = useCallback(
    (
      shapesAfterEdgeCheck: DrawingShape[],
      appliancesNewCatalog: ApplianceModel[]
    ): ProductMatchingSummary<DrawingAppliance, DrawingShape> =>
      shapesAfterEdgeCheck.reduce(
        createDrawingShapesToSummaryReducerUsingApplianceChecker(
          appliancesNewCatalog
        ),
        {
          ...INITIAL_PRODUCT_MATCHING_SUMMARY,
          newValueAfterReplacements:
            drawingTool.cloneShapes(shapesAfterEdgeCheck), // Since checker mutate array that is reduced we need to clone initial array
        }
      ),
    []
  );

  /**
   * Calculates product matching summary for corners
   */
  const calculateCornerSummary = useCallback(
    (
      shapesAfterApplianceCheck: DrawingShape[],
      cornersNewCatalog: CornerRadiusModel[],
      currentCornersCatalog: CornerRadiusModel[]
    ): ProductMatchingSummary<CornerInfoWithProductInfo, DrawingShape> =>
      shapesAfterApplianceCheck.reduce(
        createDrawingShapesToSummaryReducerUsingCornersChecker(
          cornersNewCatalog,
          currentCornersCatalog
        ),
        {
          ...INITIAL_PRODUCT_MATCHING_SUMMARY,
          newValueAfterReplacements: drawingTool.cloneShapes(
            shapesAfterApplianceCheck
          ), // Since checker mutate array that is reduced we need to clone initial array
        }
      ),
    []
  );

  /**
   * Aggregates all product matching summaries into a single summary
   */
  const aggregateSummaries = useCallback(
    ({
      edgeSummary,
      applianceSummary,
      cornerSummary,
      originalDrawingShapes,
    }: {
      edgeSummary: ProductMatchingSummary<EdgeModel, DrawingShape>;
      applianceSummary: ProductMatchingSummary<DrawingAppliance, DrawingShape>;
      cornerSummary: ProductMatchingSummary<
        CornerInfoWithProductInfo,
        DrawingShape
      >;
      originalDrawingShapes: DrawingShape[];
    }): DrawingShapesSummary => ({
      newValueAfterReplacements: cornerSummary.newValueAfterReplacements,
      valueBeforeReplacements: drawingTool.cloneShapes(originalDrawingShapes),
      kept: [
        ...edgeSummary.kept,
        ...applianceSummary.kept,
        ...cornerSummary.kept,
      ],
      replacedWithEquivalent: [
        ...edgeSummary.replacedWithEquivalent,
        ...applianceSummary.replacedWithEquivalent,
        ...cornerSummary.replacedWithEquivalent,
      ],
      removed: [
        ...edgeSummary.removed,
        ...applianceSummary.removed,
        ...cornerSummary.removed,
      ],
    }),
    []
  );

  const calculateSummaryFromDrawingShapes = useCallback(
    ({
      drawingShapes,
      newCatalogs: { edgesNewCatalog, appliancesNewCatalog, cornersNewCatalog },
      newColorBrand,
    }: {
      drawingShapes: DrawingShape[];
      newCatalogs: {
        edgesNewCatalog: EdgeModel[] | undefined;
        appliancesNewCatalog: ApplianceModel[];
        cornersNewCatalog: CornerRadiusModel[] | undefined;
      };
      newColorBrand?: string | null;
    }): { summaryFromDrawingShapes: DrawingShapesSummary } => {
      const edgeSummary = calculateEdgeSummary(
        drawingTool.cloneShapesWithoutFaux(drawingShapes),
        edgesNewCatalog ?? emptyArray,
        newColorBrand
      );

      const applianceSummary = calculateApplianceSummary(
        edgeSummary.newValueAfterReplacements,
        appliancesNewCatalog
      );

      const cornerSummary = calculateCornerSummary(
        applianceSummary.newValueAfterReplacements,
        cornersNewCatalog ?? emptyArray,
        [...cornerRadii, ...diagonalCorners]
      );

      const summaryFromDrawingShapes = aggregateSummaries({
        edgeSummary,
        applianceSummary,
        cornerSummary,
        originalDrawingShapes: drawingShapes,
      });

      return { summaryFromDrawingShapes };
    },
    [
      cornerRadii,
      diagonalCorners,
      calculateEdgeSummary,
      calculateApplianceSummary,
      calculateCornerSummary,
      aggregateSummaries,
    ]
  );

  const calculateSummaryFromTearOuts = useCallback(
    ({
      selectedTearOuts,
      tearOutsNewCatalog,
      currentTearOutsCatalog,
    }: {
      selectedTearOuts: Record<number, number>;
      tearOutsNewCatalog: TearOutModel[];
      currentTearOutsCatalog: TearOutModel[];
    }): ProductMatchingSummary<
      SelectedItemWithFullRecord,
      SelectedItemWithFullRecord
    > => {
      // Convert selected tear-outs to SelectedItemWithFullRecord[]
      const selectedTearOutsWithFullRecord = Object.entries(selectedTearOuts)
        .map(([productId, qty]): [number, SelectedItemProperties] => [
          Number(productId),
          { quantity: qty, isSelected: true, isManuallyChanged: true },
        ])
        .map(
          getFullRecordWithSavedFallback(
            currentTearOutsCatalog,
            restorableCatalog,
            tearOutsNewCatalog
          )
        )
        .filter(isSelectedItemWithPropertiesAndIsSelected);

      return selectedTearOutsWithFullRecord.reduce(
        createTearOutsToSummaryReducer(tearOutsNewCatalog),
        {
          ...INITIAL_PRODUCT_MATCHING_SUMMARY,
          newValueAfterReplacements: [...selectedTearOutsWithFullRecord],
        }
      );
    },
    [restorableCatalog]
  );

  const calculateSummaryFromCutouts = useCallback(
    ({
      selectedCutouts,
      cutoutsNewCatalog,
      currentCutoutsCatalog,
    }: {
      selectedCutouts: CustomCutoutRecord;
      cutoutsNewCatalog: CutoutModel[];
      currentCutoutsCatalog: CutoutModel[];
    }): SelectedCutoutsProductMatchingSummary => {
      const selectedCutoutsEntries = Object.entries(selectedCutouts).filter(
        ([, details]) => {
          const { onCard = 0, onShape = 0 } = details;

          return onCard + onShape > 0;
        }
      );

      const selectedCutoutsWithFullRecord = selectedCutoutsEntries
        .map(
          ([cutoutId, cutoutDetails]):
            | CustomCutoutWithFullRecord
            | undefined => {
            const cutout = findCatalogProductWithFallback({
              productId: cutoutId,
              currentCatalog: currentCutoutsCatalog,
              targetCatalog: cutoutsNewCatalog,
              savedCatalog: restorableCatalog,
            });

            return cutout
              ? {
                  cutout,
                  ...cutoutDetails,
                }
              : undefined;
          }
        )
        .filter(
          (
            cutoutWithFullRecord
          ): cutoutWithFullRecord is CustomCutoutWithFullRecord =>
            !!cutoutWithFullRecord
        ) as CustomCutoutWithFullRecord[];

      // Process each cutout through the checker
      return selectedCutoutsWithFullRecord.reduce(
        createCutoutsToSummaryReducer(cutoutsNewCatalog),
        {
          ...INITIAL_PRODUCT_MATCHING_SUMMARY,
          newValueAfterReplacements: [...selectedCutoutsWithFullRecord],
        }
      );
    },
    [restorableCatalog]
  );

  const calculateSummaryFromChipMinimizers = useCallback(
    ({
      chipMinimizersNewCatalog: newChipMin,
      currentChipMinimizerCatalog: currentChipMin,
    }: {
      chipMinimizersNewCatalog?: ChipMinimizerModel;
      currentChipMinimizerCatalog?: ChipMinimizerModel;
    }): ProductMatchingSummary<
      SelectedItemWithFullRecord,
      SelectedItemWithFullRecord
    > => {
      // Determine total selected chip minimizers: in drawing + in side panel counters
      const totalInDrawing = drawingShapes.reduce((acc, shape) => {
        shape.appliances.forEach((appliance) => {
          if (appliance.hasChipMinimizer) {
            acc += 1;
          }
        });

        return acc;
      }, 0);

      // Count from side panel counters via selector gathered at hook level
      const totalSelected = totalInDrawing + numberOfSelectedChipMinimizers;

      // If nothing selected, summary is empty
      if (!totalSelected) {
        return {
          ...INITIAL_PRODUCT_MATCHING_SUMMARY,
          newValueAfterReplacements: [],
        };
      }

      // Build a single SelectedItemWithFullRecord reflecting total quantity.
      // Target availability takes precedence because product IDs can differ by store.
      const selectedProduct = newChipMin ?? currentChipMin;
      const selectedItem: SelectedItemWithFullRecord | undefined =
        selectedProduct
          ? {
              selectedItem: selectedProduct,
              selectedItemProperties: {
                quantity: totalSelected,
                isSelected: true,
                isManuallyChanged: true,
              },
            }
          : undefined;

      if (newChipMin) {
        return {
          ...INITIAL_PRODUCT_MATCHING_SUMMARY,
          kept: selectedItem ? [selectedItem] : [],
          newValueAfterReplacements: selectedItem ? [selectedItem] : [],
        };
      }

      return {
        ...INITIAL_PRODUCT_MATCHING_SUMMARY,
        removed: selectedItem ? [selectedItem] : [],
        newValueAfterReplacements: [],
      };
    },
    [drawingShapes, numberOfSelectedChipMinimizers]
  );

  const calculateSummaryFromOptionalProducts = useCallback(
    ({
      selectedOptionalProducts,
      optionalProductsNewCatalog,
      currentOptionalProductsCatalog,
    }: {
      selectedOptionalProducts: SelectedItemMap;
      optionalProductsNewCatalog: PlumbingProduct[];
      currentOptionalProductsCatalog: OptionalProduct[];
    }): ProductMatchingSummary<
      SelectedItemWithFullRecord,
      SelectedItemWithFullRecord
    > =>
      Object.entries(selectedOptionalProducts)
        .map<SelectedItemWithFullRecord | undefined>(
          getFullRecordWithSavedFallback(
            currentOptionalProductsCatalog,
            restorableCatalog,
            optionalProductsNewCatalog
          )
        )
        .filter(isSelectedItemWithPropertiesAndIsSelected)
        .reduce<
          ProductMatchingSummary<
            SelectedItemWithFullRecord,
            SelectedItemWithFullRecord
          >
        >(
          createSelectedItemToSummaryReducer(optionalProductsNewCatalog),
          INITIAL_PRODUCT_MATCHING_SUMMARY
        ),
    [restorableCatalog]
  );

  const calculateSummaryFromPlumbing = useCallback(
    ({
      selectedPlumbing,
      plumbingNewCatalog,
      currentPlumbingCatalog,
    }: {
      selectedPlumbing: Record<number, SelectedItemProperties>;
      plumbingNewCatalog: PlumbingProduct[];
      currentPlumbingCatalog: PlumbingProduct[];
    }): ProductMatchingSummary<
      SelectedItemWithFullRecord,
      SelectedItemWithFullRecord
    > => {
      // Convert selected plumbing to SelectedItemWithFullRecord[]
      const selectedPlumbingWithFullRecord = Object.entries(selectedPlumbing)
        .map(
          getFullRecordWithSavedFallback(
            currentPlumbingCatalog,
            restorableCatalog,
            plumbingNewCatalog
          )
        )
        .filter(
          isSelectedItemWithPropertiesAndIsSelected
        ) as SelectedItemWithFullRecord[];

      return selectedPlumbingWithFullRecord.reduce(
        createPlumbingToSummaryReducer(plumbingNewCatalog),
        {
          ...INITIAL_PRODUCT_MATCHING_SUMMARY,
          newValueAfterReplacements: [...selectedPlumbingWithFullRecord],
        }
      );
    },
    [restorableCatalog]
  );

  const calculateSideApplianceReconcile = useCallback(
    (
      appliancesNewCatalog: ApplianceModel[],
      savedCatalog?: ProductCatalogMap
    ): { remap: Record<number, number>; removed: number[] } => {
      // Side-appliance sinks, cooktop, and range can only DROP (never remap) a
      // foreign id on restore — by design — because the only masterSku source is
      // the saved catalog, which restore clears to `{}`, and selected sink/appliance
      // entries carry only an id (no masterSku).
      const ids = [
        ...selectedSinksRaw.map((s) => s.id),
        ...Object.keys(selectedCooktop.selected).map(Number),
        ...Object.keys(selectedRange.selected).map(Number),
      ];

      return reconcileSideApplianceIds(ids, appliancesNewCatalog, savedCatalog);
    },
    [selectedSinksRaw, selectedCooktop, selectedRange]
  );

  /**
   * Makes the current design (defined as the drawingShapes and selectedItems (cutouts, tear-outs,etc.))
   * against a new catalog that is built from the args (zipCode, storeId, qteGrpId)
   *
   * @returns {ProductMatchingSummary}
   * */
  const makeProductMatchingSummary = useCallback(
    async ({
      zipCode,
      storeId,
      prodQteGrpID: qteGrpID,
      material,
      color,
    }: ProductMatchingTargetParameters &
      Required<
        Required<CommonProductQueryParams>['params']
      >): Promise<DesignProductMatchingSummary> => {
      const commonProductQueryParams: CommonProductQueryParams = {
        params: { zipCode, storeId, prodQteGrpID: qteGrpID },
      };

      //Fetch new catalog for param candidates (zipCode, storeId and prodQteGrpID)
      const {
        edgesNewCatalog,
        cornersNewCatalog,
        appliancesNewCatalog,
        chipMinimizersNewCatalog,
        cutoutsNewCatalog,
        tearOutsNewCatalog,
        plumbingNewCatalog,
        optionalItemsNewCatalog,
      } = await fetchNewCatalogs(commonProductQueryParams);

      const filteredEdges = selectFilteredEdges(
        edgesNewCatalog ?? emptyArray,
        material,
        color
      );

      // Summary from drawingShapes
      const { summaryFromDrawingShapes } = calculateSummaryFromDrawingShapes({
        drawingShapes,
        newCatalogs: {
          edgesNewCatalog: filteredEdges,
          appliancesNewCatalog,
          cornersNewCatalog,
        },
        newColorBrand: color?.brand,
      });

      const summaryFromOptionalProducts = calculateSummaryFromOptionalProducts({
        selectedOptionalProducts: selectedOptionalItemsAsItemMap,
        currentOptionalProductsCatalog: currentOptionalItemsCatalog,
        optionalProductsNewCatalog: optionalItemsNewCatalog,
      });

      const summaryForPlumbingSelectedItems = calculateSummaryFromPlumbing({
        selectedPlumbing,
        plumbingNewCatalog: plumbingNewCatalog ?? emptyArray,
        currentPlumbingCatalog: currentPlumbingCatalog ?? emptyArray,
      });

      const summaryFromCutouts = calculateSummaryFromCutouts({
        selectedCutouts,
        cutoutsNewCatalog: cutoutsNewCatalog ?? emptyArray,
        currentCutoutsCatalog: currentCutoutsCatalog ?? emptyArray,
      });

      const summaryFromTearOuts = calculateSummaryFromTearOuts({
        selectedTearOuts,
        tearOutsNewCatalog: tearOutsNewCatalog ?? emptyArray,
        currentTearOutsCatalog: currentTearOutsCatalog ?? emptyArray,
      });

      const summaryFromChipMinimizers = calculateSummaryFromChipMinimizers({
        chipMinimizersNewCatalog,
        currentChipMinimizerCatalog,
      });

      const sideAppliances = calculateSideApplianceReconcile(
        appliancesNewCatalog,
        restorableCatalog
      );

      return {
        drawingShapes: summaryFromDrawingShapes,
        cutouts: summaryFromCutouts,
        tearOuts: summaryFromTearOuts,
        plumbing: summaryForPlumbingSelectedItems,
        chipMinimizers: summaryFromChipMinimizers,
        selectedOptionalProducts: summaryFromOptionalProducts,
        sideAppliances,
      };
    },
    [
      fetchNewCatalogs,
      calculateSummaryFromDrawingShapes,
      drawingShapes,
      calculateSummaryFromOptionalProducts,
      selectedOptionalItemsAsItemMap,
      currentOptionalItemsCatalog,
      calculateSummaryFromPlumbing,
      selectedPlumbing,
      currentPlumbingCatalog,
      calculateSummaryFromCutouts,
      selectedCutouts,
      currentCutoutsCatalog,
      calculateSummaryFromTearOuts,
      selectedTearOuts,
      currentTearOutsCatalog,
      calculateSummaryFromChipMinimizers,
      currentChipMinimizerCatalog,
      calculateSideApplianceReconcile,
      restorableCatalog,
    ]
  );

  /**
   * Runs an equivalentProductMatchingCheck against a new catalog defined by args
   * If some args are undefined. These are inferred using defaults
   * Does not update the design
   * @returns {DesignProductMatchingSummary | null} if failing to get commonProductQueryParams
   */
  const resolveAndMakeProductMatchingSummary = useCallback(
    async (
      args: ProductMatchingTargetParameters
    ): Promise<DesignProductMatchingSummary | null> => {
      const validatedQueryParams = await resolveProductMatchingParams(args);

      return validatedQueryParams
        ? await makeProductMatchingSummary(validatedQueryParams)
        : null;
    },
    [makeProductMatchingSummary, resolveProductMatchingParams]
  );

  const clearInvalidSideItems = useCallback(
    (
      newShapes: DrawingShape[],
      originalShapes: DrawingShape[],
      materialDescription?: string
    ) => {
      if (newShapes.length === 0 || !materialDescription) {
        return;
      }

      const typesToRemove = getSideItemTypesToRemove(materialDescription);

      const shapesToProcess = originalShapes.filter((shape) =>
        newShapes.some(
          (newShape) => newShape.canvasObjectName === shape.canvasObjectName
        )
      );

      const edgesToReset = getEdgesToReset(shapesToProcess, typesToRemove);

      newShapes.forEach((shape) => {
        const resetsForThisShape = edgesToReset.filter(
          (reset) => reset.shapeName === shape.canvasObjectName
        );

        if (!shape.arrowsInfo || resetsForThisShape.length === 0) {
          return;
        }

        shape.arrowsInfo.forEach((arrow) => {
          const reset = resetsForThisShape.find(
            (item) => item.edgeName === arrow.label
          );

          if (reset) {
            arrow.isActiveForEdgeCalc = true;
            arrow.isSideItemPresent = false;
            arrow.isVisibleInCanvas = true;
          }
        });

        shape.drawingEdges.forEach((drawingEdge) => {
          const reset = resetsForThisShape.find(
            (item) => item.sideItem.arrowInfo?.drawingEdgeId === drawingEdge.id
          );

          if (reset) {
            drawingEdge.isVisibleInCanvas = true;
            drawingEdge.sideItem.type = undefined; // TODO EZE-1448
          }
        });

        if (shape.removedElementsHistory?.edgesAndCorners) {
          shape.removedElementsHistory.edgesAndCorners =
            shape.removedElementsHistory.edgesAndCorners.filter(
              (removedEdge) =>
                !resetsForThisShape.some(
                  (item) =>
                    item.sideItem.arrowInfo?.drawingEdgeId ===
                    removedEdge.drawingEdge.id
                )
            );
        }
      });
    },
    []
  );

  /**
   * Updates design with the new values after an equivalentProductMatchingCheck
   * where each product is checked against the new catalog.
   * The result is a summary that contains an array of the products kept, replacedWithEquivalent, the removed, and  the new design.
   * If failing to get commonProductQueryParams, this function does nothing.
   * @returns The updated shapes array and validated params (including matched color), or undefined if the operation failed
   * */
  const replaceProductsWithEquivalents = useCallback(
    async (
      args: ProductMatchingTargetParameters
    ): Promise<
      | {
          shapes: DrawingShape[];
          validatedParams: ProductMatchingReturnParameters;
        }
      | undefined
    > => {
      const validParams = await resolveProductMatchingParams(args);

      if (!validParams) {
        return undefined;
      }

      const summaries = await makeProductMatchingSummary(validParams);
      const hasSelectedChipMinimizers =
        numberOfSelectedChipMinimizers > 0 ||
        drawingShapes.some((shape) =>
          shape.appliances.some((appliance) => appliance.hasChipMinimizer)
        );
      const shouldClearChipMinimizers =
        hasSelectedChipMinimizers &&
        summaries.chipMinimizers?.newValueAfterReplacements.length === 0;

      // Corners equivalent replacement is handled automatically in calculateSummaryFromDrawingShapes
      // via createDrawingShapesToSummaryReducerUsingCornersChecker. The replaced corners are included
      // in summaries.drawingShapes.newValueAfterReplacements and will be applied
      // when updateDrawingShapes is called below.

      clearInvalidSideItems(
        summaries.drawingShapes.newValueAfterReplacements,
        drawingShapes,
        args.material?.description ?? undefined
      );

      const newMaterial = args.material?.description;
      const typesToRemove = getSideItemTypesToRemove(newMaterial);
      const edgesToReset = getEdgesToReset(drawingShapes, typesToRemove);

      if (edgesToReset) {
        applyEdgeResetsToShapes(
          summaries.drawingShapes.newValueAfterReplacements,
          edgesToReset
        );
      }

      // Update plumbing with replacements
      if (summaries.plumbing) {
        const newPlumbingMap =
          summaries.plumbing.newValueAfterReplacements.reduce<
            Record<number, SelectedItemProperties>
          >((acc, item) => {
            const plumbing = item.selectedItem as PlumbingProduct;

            acc[plumbing.productId] = item.selectedItemProperties;

            return acc;
          }, {});

        dispatch(updateSelectedPlumbing(newPlumbingMap));
      }

      // "Optional staging map" = restorableOptionalProducts in
      // optionalProductContext: the optional-product selections captured from a
      // saved estimate at restore time, held there until the optional catalog
      // loads and an effect "promotes" them into the live selection, then drains
      // (resets) the map. Only clear it when this reconcile actually rebuilt the
      // optional selection. A reconcile fired mid-restore (before that promotion
      // effect has drained the map) must NOT wipe still-staged optional ids —
      // that would lose legitimate current-catalog optional ids (foreign ids are
      // dropped on restore either way; we must not also lose the valid ones).
      // When the summary has no optional products to apply, leave the staging
      // map for the promotion effect to drain.
      if (
        summaries.selectedOptionalProducts?.newValueAfterReplacements.length
      ) {
        optionalProductContext?.resetRestorableOptionalProducts();
      }

      //update cutouts with replacements
      if (summaries.cutouts) {
        const newCutoutsMap =
          summaries.cutouts.newValueAfterReplacements.reduce<CustomCutoutRecord>(
            (acc, item) => {
              const { cutout, onCard, onShape } = item;

              acc[cutout.productId] = { onCard, onShape };

              return acc;
            },
            {} as CustomCutoutRecord
          );

        dispatch(setCustomCutouts({ selected: newCutoutsMap }));
      }

      if (summaries.selectedOptionalProducts) {
        const newOptionalProductsMap =
          summaries.selectedOptionalProducts.newValueAfterReplacements.reduce<
            Record<number, number>
          >((acc, item) => {
            const optionalProduct = item.selectedItem;

            acc[optionalProduct.productId] =
              item.selectedItemProperties.quantity;

            return acc;
          }, {});

        dispatch(setOptionalItemsMap(newOptionalProductsMap));
      }

      // Update tear-outs with replacements
      if (summaries.tearOuts) {
        const newTearOutsMap =
          summaries.tearOuts.newValueAfterReplacements.reduce<
            Record<number, number>
          >((acc, item) => {
            const tearOut = item.selectedItem as TearOutModel;

            acc[tearOut.productId] = item.selectedItemProperties.quantity;

            return acc;
          }, {});

        dispatch(updateSelectedTearOuts(newTearOutsMap));
      }

      // Reconcile side-panel sinks + on-card cooktop/range ids.
      // Faucets are intentionally NOT reconciled here: the faucet catalog is not
      // part of appliancesNewCatalog (it is loaded via faucetsContext, not
      // fetchAllCatalogData), so matching faucet ids here would mass-remove valid
      // faucets. Foreign faucet ids are handled go-forward by the EE-API 422 guard
      // and historically by DB remediation.
      if (summaries.sideAppliances) {
        const { remap, removed } = summaries.sideAppliances;

        if (selectedSinksRaw.length) {
          const nextSinks = computeNextSinkSelections(
            selectedSinksRaw,
            remap,
            removed
          );

          dispatch(setSelectedSinks(nextSinks));
        }

        computeChangedCookingAppliances(
          selectedCooktop,
          selectedRange,
          remap,
          removed
        ).forEach(({ selectedType, next }) => {
          dispatch(
            setCookingAppliance({
              selectedType,
              selectedAppliances: { selected: next },
            })
          );
        });
      }

      //update drawingShapes with summaries
      const shapesToUpdate = drawingTool.cloneShapes([
        ...drawingShapes.filter((shape) => shape.type === ShapeType.FAUX_SHAPE),
        ...summaries.drawingShapes.newValueAfterReplacements,
      ]);

      if (shouldClearChipMinimizers) {
        dispatch(clearChipMinimizerFlags());
        clearChipMinimizerFlagsOnShapes(shapesToUpdate);
      }

      updateDrawingShapes(shapesToUpdate);

      dispatch(updateNeedRedrawCanvas(true));

      return {
        shapes: shapesToUpdate,
        validatedParams: validParams,
      };
    },
    [
      resolveProductMatchingParams,
      makeProductMatchingSummary,
      dispatch,
      drawingShapes,
      updateDrawingShapes,
      optionalProductContext,
      numberOfSelectedChipMinimizers,
      clearInvalidSideItems,
      selectedSinksRaw,
      selectedCooktop,
      selectedRange,
    ]
  );

  return {
    replaceProductsWithEquivalents,
    resolveAndMakeProductMatchingSummary,
  };
};
